/**
 * MyEndorsementsSection — the learner's "Endorsements" card on the public
 * profile settings page. Loads GET /endorsements/me and saves each publish
 * toggle immediately (PATCH /endorsements/:id/visibility).
 */

import { useState } from 'react';
import {
  useGetMyEndorsementsQuery,
  useSetEndorsementVisibilityMutation,
} from '../../store/api/endorsementsApi';
import { EndorsementsList } from './EndorsementsList';

/**
 * `featureOn`: the server's endorsements capability (FEATURE_ENDORSEMENTS).
 * While it is off no manager can acknowledge anyone, so the card is shown
 * only to a learner who already holds endorsements (history stays theirs to
 * publish or hide) — never as a promise ("when a manager acknowledges…") of
 * something that cannot happen yet.
 */
export function MyEndorsementsSection({
  portfolioIsPublic,
  featureOn = true,
}: {
  portfolioIsPublic: boolean;
  featureOn?: boolean;
}) {
  const { data: endorsements, isLoading, isError } = useGetMyEndorsementsQuery();
  const [setVisibility] = useSetEndorsementVisibilityMutation();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = async (id: string, visibleOnPortfolio: boolean) => {
    setPendingId(id);
    setError(null);
    try {
      await setVisibility({ id, visibleOnPortfolio }).unwrap();
    } catch {
      setError('Could not update this endorsement. Please try again.');
    } finally {
      setPendingId(null);
    }
  };

  if (!featureOn && !((endorsements?.length ?? 0) > 0)) return null;

  return (
    <div className="card lg:col-span-2">
      <div className="card-header">
        <h3 className="font-semibold text-gray-900">Endorsements</h3>
        <p className="text-sm text-gray-500 mt-1">
          Acknowledgements from managers in your workspace. Each one stays private unless you choose to show it on your
          public portfolio.
        </p>
      </div>
      <div className="card-body">
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading endorsements...</p>
        ) : isError ? (
          <p className="text-sm text-red-600">Endorsements could not be loaded.</p>
        ) : (
          <EndorsementsList
            endorsements={endorsements ?? []}
            portfolioIsPublic={portfolioIsPublic}
            onToggleVisibility={handleToggle}
            pendingId={pendingId}
          />
        )}
        {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
      </div>
    </div>
  );
}

export default MyEndorsementsSection;
